import type { PolarInvoicePayments } from '../../lib/billing/polarPayments';

export const POLAR_SIDECAR_PREFIX = '/api/polar';

export const ISSUER_INCOMPLETE_ADMIN_MESSAGE =
  "L'émetteur Polar n'est pas complet. Ouvrez Polaris (Paramètres) pour renseigner la fiche, puis réessayez.";

const VAT_BP = new Set([0, 550, 850, 1000, 2000]);

export class PolarHttpError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(code: string, status: number, message?: string) {
    super(message ?? code);
    this.name = 'PolarHttpError';
    this.code = code;
    this.status = status;
  }
}

export type PolarLineInput = {
  designation: string;
  quantity: number;
  unit_ht_cents: number;
  vat_bp: number;
  nature: 'service';
};

export type CimeLine = {
  description: string;
  quantity: number;
  unit_price: number;
  /** Taux de la ligne ; absent ou null = taux du document. */
  vat_rate?: number | null;
};

export type CimeContactParty = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  postal_code: string | null;
  city: string | null;
  polar_contact_id: string | null;
  polar_organisation_id: string | null;
  siret?: string | null;
};

export type PolarClientDeps = {
  getAccessToken: () => Promise<string>;
  fetchImpl?: typeof fetch;
  persistContactPointers: (
    contactId: string,
    pointers: { polar_contact_id: string; polar_organisation_id: string | null },
  ) => Promise<void>;
  persistDocumentPointers: (
    documentId: string,
    pointers: { polar_quote_id?: string; polar_invoice_id?: string },
  ) => Promise<void>;
};

export function vatRateToBp(rate: number): number {
  const bp = Math.round(Number(rate) * 100);
  return VAT_BP.has(bp) ? bp : 2000;
}

export function cimeLinesToPolar(lines: CimeLine[], vatRate: number): PolarLineInput[] {
  return lines.map((line) => {
    const vat_bp = vatRateToBp(line.vat_rate ?? vatRate);
    const qty = Number(line.quantity);
    const unitHtCents = Math.round(Number(line.unit_price) * 100);
    const designation = String(line.description ?? '').trim() || 'Prestation';
    if (Number.isInteger(qty) && qty > 0) {
      return { designation, quantity: qty, unit_ht_cents: unitHtCents, vat_bp, nature: 'service' };
    }
    return {
      designation,
      quantity: 1,
      unit_ht_cents: Math.round(qty * unitHtCents),
      vat_bp,
      nature: 'service',
    };
  });
}

function adminMessageFor(code: string, fallback: string): string {
  if (code === 'issuer_incomplete') return ISSUER_INCOMPLETE_ADMIN_MESSAGE;
  if (code === 'polar_unreachable') return 'Polar est injoignable.';
  if (code === 'missing_credentials') {
    return 'SuperPDP n’est pas configuré dans Polar (Paramètres).';
  }
  return fallback;
}

async function polarRequest(
  path: string,
  init: RequestInit,
  deps: PolarClientDeps,
): Promise<Response> {
  const token = await deps.getAccessToken();
  const fetchImpl = deps.fetchImpl ?? fetch;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  // Un envoi de fichier (FormData) garde le type posé par le navigateur, avec sa frontière.
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  try {
    return await fetchImpl(`${POLAR_SIDECAR_PREFIX}${path}`, { ...init, headers });
  } catch {
    throw new PolarHttpError(
      'polar_unreachable',
      502,
      adminMessageFor('polar_unreachable', 'polar_unreachable'),
    );
  }
}

async function polarJson<T>(path: string, init: RequestInit, deps: PolarClientDeps): Promise<T> {
  const res = await polarRequest(path, init, deps);
  const body = (await res.json().catch(() => ({}))) as T & { error?: string; message?: string };
  if (!res.ok) {
    const code = String(body.error ?? res.statusText);
    throw new PolarHttpError(code, res.status, adminMessageFor(code, body.message ?? code));
  }
  return body;
}

function partyName(party: CimeContactParty): string {
  const company = party.company_name?.trim();
  if (company) return company;
  return `${party.first_name ?? ''} ${party.last_name ?? ''}`.trim() || 'Client';
}

function sirenFromSiret(siret: string | null | undefined): string | null {
  const digits = String(siret ?? '').replace(/\D/g, '');
  if (digits.length === 14) return digits.slice(0, 9);
  if (digits.length === 9) return digits;
  return null;
}

export async function ensurePolarParty(
  party: CimeContactParty,
  deps: PolarClientDeps,
): Promise<{ contactId: number; organisationId: number | null }> {
  if (party.polar_contact_id) {
    // Déjà dans Polar : on renvoie les coordonnées CIME (adresse, mail…) pour que
    // la pièce figée porte les mentions à jour.
    const contactId = Number(party.polar_contact_id);
    const organisationId = party.polar_organisation_id ? Number(party.polar_organisation_id) : null;
    if (organisationId && party.company_name?.trim()) {
      const current = await polarJson<{ organisation: Record<string, unknown> }>(
        `/facturation/organisations/${organisationId}`,
        { method: 'GET' },
        deps,
      );
      await polarJson(
        '/facturation/organisations',
        {
          method: 'PATCH',
          body: JSON.stringify({
            ...current.organisation,
            id: organisationId,
            legal_name: party.company_name.trim(),
            siren: sirenFromSiret(party.siret) ?? current.organisation.siren ?? null,
            address_line: party.address_line1,
            postal_code: party.postal_code,
            city: party.city,
          }),
        },
        deps,
      );
    }
    await polarJson(
      '/facturation/contacts',
      {
        method: 'PATCH',
        body: JSON.stringify({
          id: contactId,
          name: partyName(party),
          email: party.email,
          phone: party.phone,
          organisation_id: organisationId,
          address_line: party.address_line1,
          postal_code: party.postal_code,
          city: party.city,
        }),
      },
      deps,
    );
    return { contactId, organisationId };
  }

  let organisationId = party.polar_organisation_id ? Number(party.polar_organisation_id) : null;
  if (!organisationId && party.company_name?.trim()) {
    const org = await polarJson<{ organisation: { id: number } }>(
      '/facturation/organisations',
      {
        method: 'POST',
        body: JSON.stringify({
          legal_name: party.company_name.trim(),
          siren: sirenFromSiret(party.siret),
          vat_intra: null,
          address_line: party.address_line1,
          postal_code: party.postal_code,
          city: party.city,
        }),
      },
      deps,
    );
    organisationId = org.organisation.id;
  }

  const contact = await polarJson<{
    contact: { id: number; organisation_id: number | null };
  }>(
    '/facturation/contacts',
    {
      method: 'POST',
      body: JSON.stringify({
        name: partyName(party),
        email: party.email,
        phone: party.phone,
        organisation_id: organisationId,
        address_line: party.address_line1,
        postal_code: party.postal_code,
        city: party.city,
      }),
    },
    deps,
  );

  const polarOrganisationId =
    contact.contact.organisation_id != null ? String(contact.contact.organisation_id) : null;
  await deps.persistContactPointers(party.id, {
    polar_contact_id: String(contact.contact.id),
    polar_organisation_id: polarOrganisationId,
  });

  return {
    contactId: contact.contact.id,
    organisationId: contact.contact.organisation_id,
  };
}

function isoDateOrToday(value?: string | null): string {
  const trimmed = value?.trim() ?? '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  return new Date().toISOString().slice(0, 10);
}

export async function issuePolarQuote(
  input: {
    documentId: string;
    polarQuoteId: string | null;
    contact: CimeContactParty;
    title: string;
    notes: string;
    lines: CimeLine[];
    vatRate: number;
    serviceDate?: string | null;
  },
  deps: PolarClientDeps,
): Promise<{ polar_quote_id: string; number: string; status: string }> {
  const party = await ensurePolarParty(input.contact, deps);
  let quoteId = input.polarQuoteId ? Number(input.polarQuoteId) : NaN;
  if (!Number.isFinite(quoteId)) {
    const created = await polarJson<{ id: number }>(
      '/facturation/quotes/new',
      { method: 'POST' },
      deps,
    );
    quoteId = created.id;
    await deps.persistDocumentPointers(input.documentId, { polar_quote_id: String(quoteId) });
  }

  await polarJson(
    '/facturation/save',
    {
      method: 'POST',
      body: JSON.stringify({
        id: quoteId,
        contact_id: party.contactId,
        title: input.title,
        notes: input.notes,
        validity_days: 30,
        service_date: isoDateOrToday(input.serviceDate),
        lines: cimeLinesToPolar(input.lines, input.vatRate),
      }),
    },
    deps,
  );

  const finalized = await polarJson<{ number: string; status: string }>(
    '/facturation/finalize',
    { method: 'POST', body: JSON.stringify({ id: quoteId }) },
    deps,
  );

  return { polar_quote_id: String(quoteId), number: finalized.number, status: finalized.status };
}

/** Brouillon Polar (devis ou facture), sans numérotation. Le figer : `issuePolarQuote` / `issuePolarInvoice`. */
export async function upsertPolarDraft(
  input: {
    documentId: string;
    kind: 'quote' | 'invoice';
    polarId: string | null;
    contact: CimeContactParty;
    title: string;
    notes: string;
    lines: CimeLine[];
    vatRate: number;
    serviceDate?: string | null;
    dueDays?: number;
  },
  deps: PolarClientDeps,
): Promise<{ polar_id: string }> {
  const party = await ensurePolarParty(input.contact, deps);
  let polarId = input.polarId ? Number(input.polarId) : NaN;
  if (!Number.isFinite(polarId)) {
    const path = input.kind === 'invoice' ? '/facturation/invoices/new' : '/facturation/quotes/new';
    const created = await polarJson<{ id: number }>(path, { method: 'POST' }, deps);
    polarId = created.id;
    await deps.persistDocumentPointers(
      input.documentId,
      input.kind === 'invoice'
        ? { polar_invoice_id: String(polarId) }
        : { polar_quote_id: String(polarId) },
    );
  }

  const lines = cimeLinesToPolar(input.lines, input.vatRate);
  const service_date = isoDateOrToday(input.serviceDate);
  const body =
    input.kind === 'invoice'
      ? {
          id: polarId,
          contact_id: party.contactId,
          title: input.title,
          notes: input.notes,
          due_days: Number.isInteger(input.dueDays) ? input.dueDays : 0,
          service_date,
          lines,
        }
      : {
          id: polarId,
          contact_id: party.contactId,
          title: input.title,
          notes: input.notes,
          validity_days: 30,
          service_date,
          lines,
        };

  await polarJson('/facturation/save', { method: 'POST', body: JSON.stringify(body) }, deps);
  return { polar_id: String(polarId) };
}

/**
 * Fige une facture CIME directement en facture Polar (brouillon facture puis
 * finalize) : numéro de facture Polar, jamais de devis intermédiaire.
 */
export async function issuePolarInvoice(
  input: {
    documentId: string;
    polarInvoiceId: string | null;
    contact: CimeContactParty;
    title: string;
    notes: string;
    lines: CimeLine[];
    vatRate: number;
    serviceDate?: string | null;
    dueDays?: number;
  },
  deps: PolarClientDeps,
): Promise<{ polar_invoice_id: string; number: string; status: string }> {
  const { polar_id } = await upsertPolarDraft(
    {
      documentId: input.documentId,
      kind: 'invoice',
      polarId: input.polarInvoiceId,
      contact: input.contact,
      title: input.title,
      notes: input.notes,
      lines: input.lines,
      vatRate: input.vatRate,
      serviceDate: input.serviceDate,
      dueDays: input.dueDays,
    },
    deps,
  );
  const finalized = await polarJson<{ number: string; status: string }>(
    '/facturation/finalize',
    { method: 'POST', body: JSON.stringify({ id: Number(polar_id) }) },
    deps,
  );
  return { polar_invoice_id: polar_id, number: finalized.number, status: finalized.status };
}

export async function convertPolarQuoteToInvoice(
  input: {
    documentId: string;
    polarQuoteId: string;
    polarInvoiceId: string | null;
    serviceDate?: string | null;
  },
  deps: PolarClientDeps,
): Promise<{ polar_invoice_id: string; number: string; status: string }> {
  const quoteId = Number(input.polarQuoteId);
  let invoiceId = input.polarInvoiceId ? Number(input.polarInvoiceId) : NaN;
  if (!Number.isFinite(invoiceId)) {
    await polarJson(
      '/facturation/status',
      { method: 'POST', body: JSON.stringify({ id: quoteId, action: 'accept' }) },
      deps,
    );
    const converted = await polarJson<{ invoice_id: number; status: string }>(
      '/facturation/convert',
      { method: 'POST', body: JSON.stringify({ id: quoteId }) },
      deps,
    );
    invoiceId = converted.invoice_id;
    await deps.persistDocumentPointers(input.documentId, { polar_invoice_id: String(invoiceId) });
  }

  const detail = await polarJson<{
    invoice: {
      status: string;
      number: string | null;
      title: string | null;
      notes: string | null;
      due_days: number;
      service_date: string | null;
      customer: { contact_id: number | null } | null;
      lines: Array<{
        designation: string;
        quantity: number;
        unit_ht_cents: number;
        vat_bp: number;
        nature: string;
      }>;
    };
  }>(`/facturation/invoices/${invoiceId}`, { method: 'GET' }, deps);

  if (detail.invoice.status !== 'draft' && detail.invoice.number) {
    return {
      polar_invoice_id: String(invoiceId),
      number: detail.invoice.number,
      status: detail.invoice.status,
    };
  }

  const service_date = isoDateOrToday(detail.invoice.service_date ?? input.serviceDate);
  if (!detail.invoice.service_date) {
    await polarJson(
      '/facturation/save',
      {
        method: 'POST',
        body: JSON.stringify({
          id: invoiceId,
          contact_id: detail.invoice.customer?.contact_id ?? undefined,
          title: detail.invoice.title,
          notes: detail.invoice.notes ?? '',
          due_days: Number.isInteger(detail.invoice.due_days) ? detail.invoice.due_days : 0,
          service_date,
          lines: detail.invoice.lines.map((line) => ({
            designation: line.designation,
            quantity: line.quantity,
            unit_ht_cents: line.unit_ht_cents,
            vat_bp: line.vat_bp,
            nature: line.nature,
          })),
        }),
      },
      deps,
    );
  }

  const finalized = await polarJson<{ number: string; status: string }>(
    '/facturation/finalize',
    { method: 'POST', body: JSON.stringify({ id: invoiceId }) },
    deps,
  );

  return {
    polar_invoice_id: String(invoiceId),
    number: finalized.number,
    status: finalized.status,
  };
}

/** Règlements d'une facture Polaris : liste, payé, reste dû (lecture seule). */
export async function getPolarInvoicePayments(
  polarInvoiceId: string,
  deps: PolarClientDeps,
): Promise<PolarInvoicePayments> {
  const detail = await polarJson<{ invoice: PolarInvoicePayments }>(
    `/facturation/invoices/${Number(polarInvoiceId)}`,
    { method: 'GET' },
    deps,
  );
  return {
    payments: detail.invoice.payments ?? [],
    paid_cents: Number(detail.invoice.paid_cents ?? 0),
    remaining_cents: Number(detail.invoice.remaining_cents ?? 0),
  };
}

// ─── Factures de débours (Polaris) ─────────────────────────────────────────

/** Fournisseur Polaris de ce nom (comparaison sans casse), ou null. */
export async function findPolarSupplier(name: string, deps: PolarClientDeps): Promise<number | null> {
  const res = await polarJson<{ suppliers: { id: number; legal_name: string }[] }>(
    `/facturation/suppliers?q=${encodeURIComponent(name)}`,
    { method: 'GET' },
    deps,
  );
  const wanted = name.trim().replace(/\s+/g, ' ').toLowerCase();
  const hit = (res.suppliers ?? []).find((s) => s.legal_name.trim().replace(/\s+/g, ' ').toLowerCase() === wanted);
  return hit ? hit.id : null;
}

export async function createPolarSupplier(name: string, deps: PolarClientDeps): Promise<number> {
  const res = await polarJson<{ supplier: { id: number } }>(
    '/facturation/suppliers',
    { method: 'POST', body: JSON.stringify({ legal_name: name.trim() }) },
    deps,
  );
  return res.supplier.id;
}

export async function createPolarDisbursement(deps: PolarClientDeps): Promise<number> {
  const res = await polarJson<{ id: number }>('/facturation/disbursements/new', { method: 'POST' }, deps);
  return res.id;
}

export async function savePolarDisbursement(payload: object, deps: PolarClientDeps): Promise<void> {
  await polarJson('/facturation/save', { method: 'POST', body: JSON.stringify(payload) }, deps);
}

/** Identifiants des lignes d'une facture de débours, dans l'ordre. */
export async function getPolarDisbursementLineIds(id: number, deps: PolarClientDeps): Promise<number[]> {
  const res = await polarJson<{ disbursement: { lines: { id: number; position?: number }[] } }>(
    `/facturation/disbursements/${id}`,
    { method: 'GET' },
    deps,
  );
  return [...(res.disbursement.lines ?? [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((l) => l.id);
}

export async function uploadPolarDisbursementReceipt(
  id: number,
  lineId: number,
  file: Blob,
  name: string,
  deps: PolarClientDeps,
): Promise<void> {
  const form = new FormData();
  form.append('file', file, name);
  await polarJson(`/facturation/disbursements/${id}/lines/${lineId}/justificatif`, { method: 'POST', body: form }, deps);
}

export type PolarIssuerProfile = {
  legal_name?: string;
  legal_form?: string;
  capital_cents?: number;
  siret?: string;
  rcs?: string;
  vat_intra?: string;
  address_line?: string;
  postal_code?: string;
  city?: string;
  iban?: string;
  bic?: string;
  header_note?: string;
  discount_mention?: string;
  penalty_mention?: string;
  vat_regime?: string;
};

export type PolarPapier = {
  logo_path?: string | null;
  encre?: string;
  accent?: string;
  largeur_mm?: number;
  bandeau?: string;
  barre_ttc?: string;
};

export async function getPolarPaperSettings(
  deps: PolarClientDeps,
): Promise<{ issuer: PolarIssuerProfile; papier: PolarPapier }> {
  return polarJson('/facturation/settings', { method: 'GET' }, deps);
}

export async function getPolarPaperLogo(deps: PolarClientDeps): Promise<Blob | null> {
  const res = await polarRequest('/facturation/settings/papier/logo', { method: 'GET' }, deps);
  if (!res.ok) return null;
  return res.blob();
}

export async function getPolarPdf(
  polarId: number,
  deps: PolarClientDeps,
): Promise<{ bytes: ArrayBuffer; contentType: string; filename: string }> {
  const res = await polarRequest(`/facturation/pdf?id=${polarId}`, { method: 'GET' }, deps);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    const code = String(body.error ?? 'pdf_unavailable');
    throw new PolarHttpError(code, res.status, adminMessageFor(code, body.message ?? code));
  }
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(cd);
  return {
    bytes: await res.arrayBuffer(),
    contentType: res.headers.get('content-type') ?? 'application/pdf',
    filename: match?.[1] ?? 'document.pdf',
  };
}

export async function sendPolarDocument(
  polarId: number,
  deps: PolarClientDeps,
): Promise<{ status: string }> {
  return polarJson<{ status: string }>(
    '/facturation/send',
    { method: 'POST', body: JSON.stringify({ id: polarId }) },
    deps,
  );
}
